/* =========================================================================
   TelemetryStartTimeInput.tsx — UTC Start Time Picker Component
   Uses MUI DateTimePicker + dayjs.utc() standard pattern with hidden adornments
   for frictionless keyboard entry, exact UTC boundary validation, and clear reset.
   ========================================================================= */

import React, { useState, useEffect, useCallback } from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import CustomToolTip from '../../CustomToolTip';
import { formatSecToUtcIso } from '../utils/telemetry-time-range.utils';
import '../styles/telemetry-start-time-input.css';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface TelemetryStartTimeInputProps {
  valueSec: number | null;
  onChange: (newStartSec: number | null) => void;
  simStartTimeMs?: number | null;
  className?: string;
  disabled?: boolean;
}

export const TelemetryStartTimeInput: React.FC<TelemetryStartTimeInputProps> = ({
  valueSec,
  onChange,
  simStartTimeMs,
  className = '',
  disabled = false,
}) => {
  const [time, setTime] = useState<Dayjs | null>(
    valueSec != null && valueSec > 0 ? dayjs.unix(valueSec).utc() : null
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Sync external valueSec
  useEffect(() => {
    if (valueSec != null && valueSec > 0) {
      const dt = dayjs.unix(valueSec).utc();
      if (dt.isValid()) {
        setTime(dt);
        setErrorMsg(null);
        return;
      }
    }
    setTime(null);
    setErrorMsg(null);
  }, [valueSec]);

  const onInputChange = useCallback(
    (newValue: any) => {
      if (!newValue) {
        setTime(null);
        setErrorMsg(null);
        onChange(null);
        return;
      }

      // Convert dayjs input value to UTC
      const dt = dayjs(newValue).utc();
      setTime(dt);

      if (!dt.isValid()) {
        setErrorMsg('Invalid UTC datetime');
        return;
      }

      const epochSec = dt.unix();
      if (isNaN(epochSec) || epochSec <= 0) {
        setErrorMsg('Invalid datetime');
        return;
      }

      // Check against simulation start boundary
      if (simStartTimeMs != null && simStartTimeMs > 0) {
        const simStartSec = Math.floor(simStartTimeMs / 1000);
        if (epochSec < simStartSec) {
          const simStartIso = formatSecToUtcIso(simStartSec);
          setErrorMsg(`Start time cannot be before simulation start (${simStartIso})`);
          return;
        }
      }

      setErrorMsg(null);
      onChange(epochSec);
    },
    [simStartTimeMs, onChange]
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setTime(null);
      setErrorMsg(null);
      onChange(null);
    },
    [onChange]
  );

  return (
    <div className={`td-start-time-wrap ${className}`}>
      <CustomToolTip title={'YYYY-MM-DD HH:mm:ss'}>
        <div
          className={`td-start-time-pill ${errorMsg ? 'td-start-time-pill--error' : ''
            } ${valueSec != null ? 'td-start-time-pill--active' : ''}`}
        >
          <span className="td-start-time-label">Start</span>
          <span className="td-start-time-divider">|</span>
          <div className="td-start-time-picker-box">
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              adapterLocale="en"
              localeText={{ fieldHoursPlaceholder: () => 'HH' }}
            >
              <DateTimePicker
                timezone="UTC"
                disabled={disabled}
                ampm={false}
                format="YYYY-MM-DD HH:mm:ss"
                views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
                value={time}
                onChange={onInputChange}
                className="td-start-time-mui-picker"
                localeText={{ fieldHoursPlaceholder: () => 'HH' }}
                sx={{
                  width: '160px',
                  borderRadius: '0px',
                  background: 'transparent',
                  '.css-gzx5wn-MuiFormControl-root-MuiTextField-root': {
                    width: 'auto !important'
                  },
                  '.MuiFormControl-root': {
                    paddingRight: '0px',
                  },
                  '.MuiInputBase-input': {
                    fontSize: '12px !important',
                    color: '#EEEEEE !important',
                    fontFamily: 'var(--font-body, "Montserrat", sans-serif) !important',
                    height: '24px !important',
                    padding: '0 4px !important',
                    paddingLeft: '0px !important',
                  },
                  '.MuiOutlinedInput-input': {
                    fontSize: '12px !important',
                    height: '24px !important',
                    padding: '0 4px !important',
                    paddingLeft: '0px !important',
                    fontFamily: 'var(--font-body, "Montserrat", sans-serif) !important',
                  },
                  '.MuiOutlinedInput-root': {
                    height: '24px !important',
                    minHeight: '24px !important',
                    paddingRight: '0px !important',
                    background: 'transparent !important',
                  },
                  '.MuiInputAdornment-root': {
                    display: 'none !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none !important',
                  },
                  '.Mui-disabled': {
                    color: 'rgba(240,240,240,0.30) !important',
                    '-webkit-text-fill-color': 'rgba(240,240,240,0.30) !important',
                    cursor: 'auto',
                  },
                }}
              />
            </LocalizationProvider>
          </div>
          {valueSec != null && (
            <button
              type="button"
              className="td-start-time-clear-btn"
              onClick={handleClear}
              title="Clear start time"
            >
              ✕
            </button>
          )}
        </div>
      </CustomToolTip>
      {errorMsg && <div className="td-start-time-error-text">{errorMsg}</div>}
    </div>
  );
};

export default TelemetryStartTimeInput;
